import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ClientGroup, Nullable } from "types";
import { useClientsState } from "atoms/clientsAtom";
import { useGetClientGroups } from "features/ClientGroups/api";

import Radio from "components/Radio/Radio";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { MediumText } from "components/MediumText/MediumText";
import { NamedCheckbox } from "components/NamedCheckbox/NamedCheckbox";
import { NamedInput } from "components/NamedInput/NamedInput";
import { RegularText } from "components/RegularText/RegularText";

import { Footer, IndividualClient, OOOIp, PersonalAccount } from "./components";
import styles from "./styles.module.css";
import { AddClientParams } from "./types";

enum ClientType {
  Individual = 0,
  OOO = 1,
  IP = 2,
}

const clientTypes = [
  { type: ClientType.Individual, text: "Физическое лицо" },
  { type: ClientType.OOO, text: "ООО" },
  { type: ClientType.IP, text: "ИП" },
];

type Props = {
  isEdit?: boolean;
};

export type ClientInfoForm = AddClientParams & {
  group: Nullable<ClientGroup>;
  clientid: string;
  bank: string;
};

export type ClientDiscountForm = {
  startpercent: string;
  discountpercent: string;
  shiftpercent: string;
  endpercent: string;
  shiftpercentsum: string;
  nextdiscountsum: string;
  startbonus: string;
  bonuspercent: string;
  startbonussum: string;
  shiftbonus: string;
  endbonus: string;
  maxbonuspay: string;
  shiftbonussum: string;
  nextbonussum: string;
  clientaccountid: string;
};

export const ClientInfo = ({ isEdit }: Props): JSX.Element => {
  const { clientsState } = useClientsState();

  const { control, setValue, watch, handleSubmit, reset } =
    useForm<ClientInfoForm>({
      defaultValues: {
        firstname: "",
        secondname: "",
        otc: "",
        phone: "",
        address: "",
        card: "",
        comment: "",
        createtime: "",
        importcode: "",
        createcomment: "",
        city: "",
        sex: "1",
        house: "",
        korpus: "",
        podezd: "",
        flat: "",
        birthday: "",
        avaliable: 0,
        firma: 0,
        inn: "",
        kpp: "",
        streetid: 0,
        eachbouquetdiscount: 0,
        eachbouquet: 0,
        defaultburnbonus: 0,
        pricelistid: undefined,
        clientid: "",
        email: "",
        // group: null,
        notverifysms: 0,
        bank: "",
      },
    });

  const {
    handleSubmit: handleClientDiscountSubmit,
    control: clientDiscountControl,
    reset: clientDiscountReset,
  } = useForm<ClientDiscountForm>({
    defaultValues: {
      startpercent: "",
      discountpercent: "",
      shiftpercent: "",
      endpercent: "",
      shiftpercentsum: "",
      nextdiscountsum: "",
      startbonus: "",
      bonuspercent: "",
      startbonussum: "",
      shiftbonus: "",
      endbonus: "",
      maxbonuspay: "",
      shiftbonussum: "",
      nextbonussum: "",
      clientaccountid: "",
    },
  });

  const clientType = watch("firma");

  const { data: clientGroup = [] } = useGetClientGroups({ enabled: false });

  useEffect(() => {
    if (isEdit && clientsState.activeItem?.clientgroupid) {
      const selectedGroup = clientGroup.find(
        (temp: ClientGroup) =>
          temp.clientgroupid === clientsState.activeItem?.clientgroupid
      );

      reset({
        firstname: clientsState.activeItem.firstname,
        secondname: clientsState.activeItem.secondname,
        otc: clientsState.activeItem.otc,
        phone: clientsState.activeItem.phone,
        address: clientsState.activeItem.address,
        card: clientsState.activeItem.card,
        comment: clientsState.activeItem.comment,
        createtime: clientsState.activeItem.createtime,
        importcode: clientsState.activeItem.importcode,
        createcomment: clientsState.activeItem.createcomment,
        city: clientsState.activeItem.city,
        sex: clientsState.activeItem.sex + "",
        house: clientsState.activeItem.house,
        korpus: clientsState.activeItem.korpus,
        podezd: clientsState.activeItem.podezd,
        flat: clientsState.activeItem.flat,
        birthday: clientsState.activeItem.birthday,
        avaliable: clientsState.activeItem.avaliable,
        firma: clientsState.activeItem.firma,
        inn: clientsState.activeItem.inn,
        kpp: clientsState.activeItem.kpp,
        streetid: clientsState.activeItem.streetid,
        eachbouquetdiscount: clientsState.activeItem.eachbouquetdiscount,
        eachbouquet: clientsState.activeItem.eachbouquet,
        defaultburnbonus: clientsState.activeItem.defaultburnbonus,
        pricelistid: Number(clientsState.activeItem.pricelistid),
        clientid: clientsState.activeItem.clientid,
        email: clientsState.activeItem.email,
        notverifysms: +clientsState.activeItem.notverifysms || 0,
        group: selectedGroup!,
        bank: clientsState.activeItem.bank || "",
      });
    } else {
      setValue("group", clientsState.activeGroupItem);
    }
  }, [clientGroup, clientsState, isEdit]);

  return (
    <BaseLayout
      paddings={[24, 42]}
      width={940}
      header={
        <ModalHeader
          title={
            isEdit ? clientsState.activeItem?.firstname || "" : "Новый клиент"
          }
        />
      }
      footer={
        <Footer
          handleSubmit={handleSubmit}
          isEdit={isEdit}
          handleClientDiscountSubmit={handleClientDiscountSubmit}
        />
      }
    >
      <div className="flex items-center">
        <div className="not-first:ml-6">
          <RegularText size={14}>
            Группа:&nbsp;&nbsp;
            <span className="text-purple-500">
              {watch("group")?.name || "Группа удалена"}
            </span>
          </RegularText>
        </div>
        <div className="not-first:ml-6">
          <Controller
            name="avaliable"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                {...field}
                name="Активен"
                checked={!!field.value}
              />
            )}
          />
        </div>
        <div className="not-first:ml-6">
          <Controller
            name="notverifysms"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                {...field}
                name="Не подтверждать клиента по СМС"
                checked={!!field.value}
              />
            )}
          />
        </div>
        {isEdit && (
          <div className="not-first:ml-6">
            <Controller
              name="clientid"
              control={control}
              render={({ field }) => (
                <NamedInput {...field} className="w-16" label="Код" disabled />
              )}
            />
          </div>
        )}
      </div>
      <div className="flex mt-8">
        {clientTypes.map((tempClientType) => (
          <div className="not-first:ml-6" key={tempClientType.type}>
            <Radio
              id={String(tempClientType.type)}
              text={tempClientType.text}
              name="client-type"
              onChange={() => setValue("firma", tempClientType.type)}
              checked={tempClientType.type === clientType}
              value={tempClientType.type}
            />
          </div>
        ))}
      </div>
      <div className={styles.clientDataWrapper}>
        {clientType === ClientType.Individual && (
          <IndividualClient control={control} />
        )}
        {clientType === ClientType.OOO && (
          <OOOIp title="ООО" control={control} />
        )}
        {clientType === ClientType.IP && <OOOIp title="ИП" control={control} />}
      </div>
      <PersonalAccount
        control={control}
        isEdit={isEdit}
        clientDiscountControl={clientDiscountControl}
        clientDiscountReset={clientDiscountReset}
      />
    </BaseLayout>
  );
};
